import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Footer, Header, SeoHelmet, useDemoModal, useDemoVariant } from 'components';
import { gsap, ScrollTrigger, useGSAP } from 'src/utils/gsap';
import { scrollToTarget } from 'src/utils/smoothScroll';

import { PageContext, PAGES } from './pages';
import Agent from './sections/Agent';
import Faq from './sections/Faq';
import Film from './sections/Film';
import Final from './sections/Final';
import Hero from './sections/Hero';
import HowItWorks from './sections/HowItWorks';
import Manifesto from './sections/Manifesto';
import Problems from './sections/Problems';
import Results from './sections/Results';

const TONES = { dark: '#02231F', light: '#EEEFE2' };

// page: variant from PAGES, e.g. 'veterinaro' for the partner page
const LandingPage = ({ page = 'home' }) => {
  const { t } = useTranslation('page');
  const { hash } = useLocation();
  const openDemo = useDemoModal();
  const mainRef = useRef(null);
  const config = PAGES[page];

  useDemoVariant(config.modal);

  // Page background fades between brand green and cream following the section in the middle of the screen
  useGSAP(
    () => {
      const html = document.documentElement;
      let tone = 'dark';

      const setTone = (next) => {
        if (next === tone) return;
        tone = next;
        gsap.to(document.body, { backgroundColor: TONES[next], duration: 0.8, ease: 'power2.out', overwrite: true });
        html.classList.toggle('nav-light', next === 'light');
      };

      const triggers = gsap.utils.toArray('[data-tone]', mainRef.current).map((section) =>
        ScrollTrigger.create({
          trigger: section,
          start: 'top 50%',
          end: 'bottom 50%',
          onToggle: (self) => self.isActive && setTone(section.dataset.tone)
        })
      );

      // After every recalculation (resize, fonts) use the section that really is in the middle
      const onRefresh = () => {
        const active = [...triggers].reverse().find((trigger) => trigger.isActive);
        if (active) setTone(active.trigger.dataset.tone);
      };
      const refresh = () => ScrollTrigger.refresh();

      html.classList.add('js-bg');
      ScrollTrigger.addEventListener('refresh', onRefresh);
      window.addEventListener('load', refresh);
      document.fonts?.ready.then(refresh);

      return () => {
        ScrollTrigger.removeEventListener('refresh', onRefresh);
        window.removeEventListener('load', refresh);
        gsap.killTweensOf(document.body);
        gsap.set(document.body, { clearProps: 'backgroundColor' });
        html.classList.remove('js-bg', 'nav-light');
      };
    },
    { scope: mainRef }
  );

  // Links like /#faq from other pages, /contact opens the demo form
  useEffect(() => {
    if (hash === '#demo') {
      openDemo();
      return;
    }
    const target = hash && document.getElementById(hash.slice(1));
    if (!target) return;
    const raf = requestAnimationFrame(() => scrollToTarget(target, { offset: -20 }));
    return () => cancelAnimationFrame(raf);
  }, [hash]);

  return (
    <PageContext value={config}>
      <SeoHelmet
        title={t(`${page}.seo.title`)}
        description={t(`${page}.seo.description`)}
      />
      <Header
        homePath={config.path}
        partner={config.partner}
        ctaLabel={t(`${page}.cta`)}
      />
      <main ref={mainRef}>
        <Hero />
        <Agent />
        <Manifesto />
        <Film />
        <Problems />
        <HowItWorks />
        <Results />
        <Faq />
        <Final />
      </main>
      <Footer
        homePath={config.path}
        social={config.social}
      />
    </PageContext>
  );
};

export default LandingPage;
